import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { LockIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  readonly label?: ReactNode;
  /**
   * F26 — locked cell state. When true the switch is forced on, non-interactive,
   * and rendered in the success tone with a small lock icon on the knob.
   * Used by the ChannelMatrix's REQUIRED rows (transactional / security
   * categories that cannot be opted out of).
   */
  readonly locked?: boolean;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, className, disabled, locked, checked, defaultChecked, ...rest },
  ref,
) {
  // Locked = force checked + disabled at the DOM level, but render the success
  // visual (not the disabled opacity).
  const effectiveChecked = locked ? true : checked;
  const effectiveDisabled = locked ? true : disabled;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none text-ink',
        'text-[13.5px]',
        disabled && !locked && 'opacity-45 cursor-not-allowed',
        locked && 'cursor-default',
        className,
      )}
    >
      <span className="relative inline-flex">
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          disabled={effectiveDisabled}
          checked={effectiveChecked}
          defaultChecked={effectiveChecked === undefined ? defaultChecked : undefined}
          aria-readonly={locked ? true : undefined}
          className={cn(
            'peer appearance-none w-9 h-5 rounded-full bg-hairline-2 border border-hairline-2',
            'transition-[background-color,box-shadow] duration-[160ms] ease-[cubic-bezier(.4,0,.2,1)]',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
            'checked:bg-accent checked:border-accent',
            'disabled:cursor-not-allowed',
            locked && '!bg-success !border-success',
          )}
          {...rest}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-0.5 start-0.5 size-4 rounded-full bg-surface',
            'shadow-[0_1px_3px_rgba(0,0,0,0.18)]',
            'transition-transform duration-[160ms] ease-[cubic-bezier(.4,0,.2,1)]',
            'peer-checked:translate-x-4 rtl:peer-checked:-translate-x-4',
            'flex items-center justify-center',
            locked && 'text-success',
          )}
        >
          {locked ? <LockIcon size={8} color="currentColor" /> : null}
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
});
